import { useNavigate } from "react-router-dom";
import arrowLeft from "./../../assets/images/arrow-left.svg";
import noLogs from "./../../assets/images/no-logs.svg";
import PatientLogCard from "../home/components/PatientLogCard";
import Footer from "../home/components/Footer";
import useReferredPatients from "../../hooks/useReferredPatients";
import { useEffect, useState, useCallback, useRef } from "react";
import SearchComponent from "../patients/components/SearchComponent";

const SEARCH_DEBOUNCE_MS = 500;

const PatientsLogs = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);

  const {
    referredPlanData,
    resetAndFetch,
    loadMoreData,
    loader,
    loadingMore,
    hasMoreData,
  } = useReferredPatients();

  // Debounce search to avoid API call on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    resetAndFetch(debouncedSearchTerm);
  }, [debouncedSearchTerm, resetAndFetch]);

  const backBtnHandler = () => {
    navigate("/remote-care");
  };

  // Infinite scroll implementation
  const handleLoadMore = useCallback(() => {
    if (hasMoreData && !loadingMore) {
      loadMoreData(debouncedSearchTerm);
    }
  }, [hasMoreData, loadingMore, loadMoreData, debouncedSearchTerm]);

  useEffect(() => {
    const currentTrigger = loadMoreTriggerRef.current;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "20px",
      }
    );

    if (currentTrigger) {
      observerRef.current.observe(currentTrigger);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleLoadMore]);

  // Cleanup observer on component unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <>
      <div className="border-b border-[#EAECF0] sticky top-0 bg-white z-[10]">
        <div className="flex items-start font-semibold gap-x-2 p-6">
          <img
            height={22}
            width={22}
            src={arrowLeft}
            alt="arrow-left"
            onClick={backBtnHandler}
          />
          <span>Patient's Logs</span>
        </div>
      </div>
      <div className="bg-[#F9F9FF] min-h-screen">
        {(searchTerm && !referredPlanData?.length) ||
        referredPlanData?.length ? (
          <>
            <SearchComponent onSearch={setSearchTerm} searchTerm={searchTerm} />
            {!loader && (
              <main className="px-6 pb-24">
                {referredPlanData?.map((patient, index) => (
                  <PatientLogCard
                    key={`${patient.id}-${index}`}
                    patient={patient}
                  />
                ))}

                {/* Infinite scroll trigger */}
                {hasMoreData && (
                  <div ref={loadMoreTriggerRef} className="w-full h-4" />
                )}

                {/* Loading more indicator */}
                {loadingMore && (
                  <div className="flex justify-center items-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="ml-2 text-gray-600">
                      Loading more patients...
                    </span>
                  </div>
                )}
              </main>
            )}
            {!referredPlanData?.length && !loader && (
              <span className="flex justify-center items-center h-[50vh] text-gray-800 font-poppins font-medium">
                No patients found!
              </span>
            )}
          </>
        ) : (
          !loader && (
            <section className="flex flex-col w-full pt-20 pb-6 pl-6 pr-6">
              <img
                loading="lazy"
                src={noLogs}
                alt=""
                className="object-contain self-center max-w-full aspect-[1.25]"
              />
              <div className="flex flex-col justify-center w-full">
                <div className="flex flex-col justify-center items-center w-full">
                  <h2 className="text-2xl font-semibold leading-none text-gray-800">
                    No Patients Logs!
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-center text-gray-700">
                    Start recommending care plans to your patients and track
                    your patients activity & improvement
                  </p>
                </div>
                {/* <button
                  className="flex overflow-hidden gap-2 justify-center items-center px-4 py-4 mt-4 w-full text-lg font-medium leading-none text-center text-white bg-indigo-600 rounded-2xl min-h-[55px]"
                  onClick={referHandler}
                >
                  <span className="self-stretch my-auto">
                    Refer Care Plan Now
                  </span>
                  <img
                    loading="lazy"
                    src={arrowRight}
                    alt=""
                    className="object-contain shrink-0 self-stretch my-auto w-6 aspect-square"
                  />
                </button> */}
              </div>
            </section>
          )
        )}

        {/* Initial loading indicator */}
        {loader && (
          <div className="flex justify-center items-center h-[50vh]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="ml-3 text-gray-600">Loading patients...</span>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default PatientsLogs;
